/**
 * Tag Editor Event Service
 * Centralized event handling for tag editor DOM events
 */

import { Injectable, NgZone } from '@angular/core'
import {
  BUTTON_TAG_NAMES,
  COMPONENT_STRINGS,
  CSS_CLASSES,
  CSS_SELECTORS,
  HTML_ATTRIBUTES,
  HTML_ELEMENT_SELECTORS,
  HTML_VALUES,
  KEYBOARD_KEYS,
  NAVIGATION_KEYS,
  NODE_TYPES,
  TAG_EDITOR_COLORS,
  TAG_EDITOR_STRINGS,
  TAG_EDITOR_VALUES,
} from '../tag-editor.constants'
import { EditorInteractionService } from './editor-interaction.service'
import { FormattingService } from './formatting.service'
import { PasteService } from './paste.service'
import { TagManagerService } from './tag-manager.service'
import { TagEditorStateService } from './tag-editor-state.service'
import { DebugLoggingService } from './debug-logging.service'
import { TagEditorTreeNode } from '../tag-editor-tree-node'
import { InsertedTagCustom } from '../tag-editor.model'

/**
 * Callbacks for event handling
 */
export interface EventCallbacks {
  readonly onContentChange: (content: string) => void
  readonly onTagEdit: (tagElement: HTMLElement, clickRange?: Range) => void
  readonly onTagEditExit: (save: boolean) => void
  readonly onSelectionChange: () => void
  readonly onFormattingChange: () => void
  readonly onTagInsert: (node: TagEditorTreeNode, text: string) => void
  readonly onTagRemove: (tagId: string) => void
  readonly onTagClick: (tag: InsertedTagCustom) => void
  readonly onTagInEditorClick: (tagId: string) => void
  readonly onSaveButtonClick: () => void
  readonly onContentUpdate: (content: string) => void
  readonly onTagsParse: () => void
  readonly getEditorHTML: () => string
  readonly getSelectedText: () => string
  readonly debouncedEmitChange: (content: string) => void
  readonly onBlurSave: () => void
  readonly getTreeNodes: () => ReadonlyArray<TagEditorTreeNode>
}

/**
 * Context for event handling
 */
export interface EditorContext {
  readonly editor: HTMLElement
  readonly stateService: TagEditorStateService
  readonly tagManagerService: TagManagerService
  readonly formattingService: FormattingService
  readonly pasteService: PasteService
  readonly editorInteractionService: EditorInteractionService
  readonly callbacks: EventCallbacks
  readonly isSelectingText: { value: boolean }
}

/**
 * Cleanup function for event listeners
 */
export type CleanupFunction = () => void

@Injectable({ providedIn: 'root' })
export class TagEditorEventService {
  constructor(
    private readonly ngZone: NgZone,
    private readonly debugLoggingService: DebugLoggingService,
  ) {}

  private debugLog(message: string, ...args: ReadonlyArray<unknown>): void {
    this.debugLoggingService.logWithService(COMPONENT_STRINGS.COMPONENT_NAME, message, ...args)
  }

  /**
   * Sets up all event listeners for the editor
   */
  public setupEventListeners(context: EditorContext): CleanupFunction {
    const { editor, callbacks, stateService, isSelectingText } = context

    this.debugLog('🔧 Setting up event listeners on editor:', {
      contentEditable: editor.contentEditable,
      tagName: editor.tagName,
    })

    // Input event - detect changes
    const inputHandler = () => callbacks.onContentChange(callbacks.getEditorHTML())
    editor.addEventListener('input', inputHandler)

    // Paste event - handle tag preservation
    const pasteHandler = (e: ClipboardEvent) => this.handlePaste(e, context)
    editor.addEventListener('paste', pasteHandler)

    // Keydown event - handle special keys
    const keydownHandler = (e: KeyboardEvent) => this.handleKeydown(e, context)
    editor.addEventListener('keydown', keydownHandler)

    // Unified click/dblclick handler
    const clickHandler = (e: MouseEvent) => this.handleClick(e, context)
    editor.addEventListener('click', clickHandler)
    const dblclickHandler = (e: MouseEvent) => this.handleDoubleClick(e, context)
    editor.addEventListener('dblclick', dblclickHandler)

    // Mouse events for selection tracking
    const mousedownHandler = (e: MouseEvent) => this.handleMouseDown(e, context)
    editor.addEventListener('mousedown', mousedownHandler)
    const mousemoveHandler = (e: MouseEvent) => this.handleMouseMove(e, context)
    editor.addEventListener('mousemove', mousemoveHandler)
    const mouseupHandler = (e: MouseEvent) => this.handleMouseUp(e, context)
    editor.addEventListener('mouseup', mouseupHandler)

    // Copy event - handle tag preservation
    const copyHandler = (e: ClipboardEvent) => this.handleCopy(e, context)
    editor.addEventListener('copy', copyHandler)

    // Selection change - update formatting state and text selection
    const selectionChangeHandler = (): void => {
      const isEditorActive: boolean = document.activeElement === editor
      const isSelecting: boolean = isSelectingText.value
      const selection: Selection | null = window.getSelection()
      const hasRange: boolean = !!(selection && selection.rangeCount > 0)
      const anchorNode: Node | null = hasRange ? selection!.anchorNode : null
      const hasSelectionInEditor: boolean = !!(anchorNode && editor.contains(anchorNode))

      this.debugLog(' selectionchange event:', {
        isEditorActive,
        isSelecting,
        hasRange,
        hasSelectionInEditor,
        activeElement: document.activeElement?.tagName,
        editorTag: editor.tagName,
        selectedText: selection?.toString() || '',
        anchorNodeName: anchorNode?.nodeName,
      })

      if (hasSelectionInEditor && !isSelecting) {
        this.debugLog(' selectionchange - updating state (selection in editor)')
        callbacks.onSelectionChange()
        callbacks.onFormattingChange()
      } else if (isEditorActive && !isSelecting) {
        this.debugLog(' selectionchange - updating state (editor active)')
        callbacks.onSelectionChange()
        callbacks.onFormattingChange()
      } else {
        this.debugLog(' selectionchange - skipped:', {
          reason: !hasSelectionInEditor && !isEditorActive ? 'selection not in editor' : 'isSelecting=' + isSelecting,
        })
      }
    }
    document.addEventListener('selectionchange', selectionChangeHandler)

    // Editor blur listener - save pending changes when editor loses focus
    const blurHandler = (event: FocusEvent): void => {
      this.debugLog(' Editor blur event detected - checking for pending changes', {
        relatedTarget: (event.relatedTarget as HTMLElement)?.tagName,
        activeElement: document.activeElement?.tagName,
      })

      const editingTag = stateService.getEditingTag()
      const relatedTarget: HTMLElement | null = event.relatedTarget as HTMLElement | null
      const activeElement: Element | null = document.activeElement
      const isToolbarButton: boolean =
        relatedTarget?.classList.contains(CSS_CLASSES.TOOLBAR_BUTTON) === true ||
        activeElement?.classList.contains(CSS_CLASSES.TOOLBAR_BUTTON) === true ||
        relatedTarget?.closest(`.${CSS_CLASSES.TOOLBAR_BUTTON}`) !== null ||
        activeElement?.closest(`.${CSS_CLASSES.TOOLBAR_BUTTON}`) !== null

      const isButton: boolean =
        relatedTarget?.tagName === BUTTON_TAG_NAMES.ION_BUTTON ||
        relatedTarget?.tagName === BUTTON_TAG_NAMES.BUTTON ||
        activeElement?.tagName === BUTTON_TAG_NAMES.ION_BUTTON ||
        activeElement?.tagName === BUTTON_TAG_NAMES.BUTTON ||
        relatedTarget?.closest(BUTTON_TAG_NAMES.ION_BUTTON_LOWERCASE) !== null ||
        activeElement?.closest(BUTTON_TAG_NAMES.ION_BUTTON_LOWERCASE) !== null ||
        isToolbarButton

      if (editingTag) {
        if (isButton) {
          this.debugLog(' Editor blur - focus moved to button, saving changes immediately')
          queueMicrotask(() => {
            const currentEditingTag = stateService.getEditingTag()
            if (currentEditingTag?.element === editingTag.element) {
              callbacks.onTagEditExit(true)
            }
          })
        } else {
          // Use queueMicrotask to allow inline edit to complete before checking state
          queueMicrotask(() => {
            const currentEditingTag = stateService.getEditingTag()
            if (currentEditingTag?.element === editingTag.element) {
              this.debugLog(' Editor blur - saving changes after microtask')
              callbacks.onTagEditExit(true)
            }
          })
        }
      } else {
        // Check state immediately (synchronously) - no setTimeout needed
        // Skip saving if tag selector is open or tag insertion is in progress
        if (stateService.getTagSelectorOpen() || stateService.getInsertingTag()) {
          this.debugLog(' Editor blur - skipped (tag selector open or inserting tag)', {
            selectorOpen: stateService.getTagSelectorOpen(),
            insertingTag: stateService.getInsertingTag(),
          })
          return
        }
        // Skip saving if focus moved to toolbar button (prevents toolbar corruption)
        if (isToolbarButton) {
          this.debugLog(' Editor blur - skipped (focus moved to toolbar button)')
          return
        }
        this.debugLog(' Editor blur - saving pending changes (no active edit)')
        callbacks.onBlurSave()
      }
    }
    editor.addEventListener('blur', blurHandler)

    // Global click listener to detect save button clicks
    const globalClickListener = (event: MouseEvent): void => {
      const target = event.target as HTMLElement | null
      if (!target) {
        return
      }

      const buttonElement: HTMLElement | null =
        target.closest(BUTTON_TAG_NAMES.ION_BUTTON_LOWERCASE) ||
        target.closest(BUTTON_TAG_NAMES.BUTTON_LOWERCASE) ||
        (target.tagName === BUTTON_TAG_NAMES.ION_BUTTON ? target : null) ||
        (target.tagName === BUTTON_TAG_NAMES.BUTTON ? target : null)

      if (!buttonElement) {
        return
      }

      const hasSaveIcon: boolean = buttonElement.querySelector(HTML_ELEMENT_SELECTORS.ION_ICON_SAVE) !== null

      if (hasSaveIcon) {
        const editingTag = stateService.getEditingTag()
        if (editingTag) {
          this.debugLog(' Global click detected on save button - saving pending changes', {
            hasSaveIcon,
          })
          callbacks.onTagEditExit(true)
        }
      }
    }
    document.addEventListener('mousedown', globalClickListener, true)

    // Return cleanup function
    return (): void => {
      editor.removeEventListener('input', inputHandler)
      editor.removeEventListener('paste', pasteHandler)
      editor.removeEventListener('keydown', keydownHandler)
      editor.removeEventListener('click', clickHandler)
      editor.removeEventListener('dblclick', dblclickHandler)
      editor.removeEventListener('mousedown', mousedownHandler)
      editor.removeEventListener('mousemove', mousemoveHandler)
      editor.removeEventListener('mouseup', mouseupHandler)
      editor.removeEventListener('copy', copyHandler)
      editor.removeEventListener('blur', blurHandler)
      document.removeEventListener('selectionchange', selectionChangeHandler)
      document.removeEventListener('mousedown', globalClickListener, true)
    }
  }

  /**
   * Creates a range from caret position at click coordinates
   */
  private createRangeFromCaretPosition(clientX: number, clientY: number, editor: HTMLElement): Range | null {
    if (typeof document.caretPositionFromPoint !== 'function') {
      return null
    }

    try {
      const caretPosition = document.caretPositionFromPoint(clientX, clientY)
      if (caretPosition && caretPosition.offsetNode) {
        if (editor.contains(caretPosition.offsetNode)) {
          const range: Range = document.createRange()
          range.setStart(caretPosition.offsetNode, caretPosition.offset)
          range.setEnd(caretPosition.offsetNode, caretPosition.offset)
          return range
        }
      }
    } catch (error) {
      this.debugLog('createRangeFromCaretPosition: caretPositionFromPoint failed:', error)
    }

    return null
  }

  /**
   * Handles click events for cursor positioning
   */
  public handleClick(event: MouseEvent, context: EditorContext): void {
    if (event.detail !== 1) {
      return
    }

    const { editor, editorInteractionService, callbacks } = context

    this.debugLog(' onEditorClick:', {
      target: (event.target as HTMLElement)?.tagName,
      isSelectingText: context.isSelectingText.value,
    })

    const targetElement: HTMLElement | null = event.target as HTMLElement
    const clickedTagElement: HTMLElement | null = targetElement
      ? editorInteractionService.findTagElement(targetElement, editor)
      : null

    if (clickedTagElement) {
      const tagId: string | null = clickedTagElement.getAttribute(HTML_ATTRIBUTES.DATA_TAG_ID)
      if (tagId) {
        callbacks.onTagInEditorClick(tagId)
      }
      this.debugLog(' onEditorClick - clicked on tag element, positioning cursor at click location')

      const selection: Selection | null = window.getSelection()
      if (selection) {
        let range: Range | null = this.createRangeFromCaretPosition(event.clientX, event.clientY, editor)

        if (!range) {
          range = editorInteractionService.createRangeFromPoint(event.clientX, event.clientY, editor)
        }

        // If range is inside tag element, position cursor inside tag at click location
        // This allows editing the tag text directly without needing to double-click
        if (range && clickedTagElement.contains(range.startContainer)) {
          // Check if we clicked on text node inside tag (not on tag border/padding)
          const isTextNodeInsideTag: boolean =
            range.startContainer.nodeType === NODE_TYPES.TEXT_NODE && clickedTagElement.contains(range.startContainer)

          if (isTextNodeInsideTag) {
            // Position cursor inside tag at click location for direct editing
            requestAnimationFrame(() => {
              try {
                selection.removeAllRanges()
                selection.addRange(range!)
                this.debugLog(' onEditorClick - cursor positioned inside tag at click location')
              } catch (error) {
                this.debugLog('onEditorClick (tag): Could not apply selection:', error)
                // Fallback: position after tag
                editorInteractionService.positionCursorAfterTag(clickedTagElement)
              }
            })
          } else {
            // Clicked on tag border/padding - position cursor based on click position
            const tagRect: DOMRect = clickedTagElement.getBoundingClientRect()
            const clickX: number = event.clientX
            const tagCenterX: number = tagRect.left + tagRect.width / 2

            if (clickX < tagCenterX) {
              editorInteractionService.positionCursorBeforeTag(clickedTagElement)
              this.debugLog(' onEditorClick - cursor positioned before tag (left side click)')
            } else {
              editorInteractionService.positionCursorAfterTag(clickedTagElement)
              this.debugLog(' onEditorClick - cursor positioned after tag (right side click)')
            }
          }
        } else if (range) {
          requestAnimationFrame(() => {
            try {
              selection.removeAllRanges()
              selection.addRange(range!)
              this.debugLog(' onEditorClick - cursor positioned at click location using Selection API')
            } catch (error) {
              this.debugLog('onEditorClick (tag): Could not apply selection:', error)
            }
          })
        } else {
          editorInteractionService.positionCursorAfterTag(clickedTagElement)
          this.debugLog(' onEditorClick - fallback: cursor positioned after tag')
        }
      }

      callbacks.onFormattingChange()

      setTimeout(() => {
        callbacks.onSelectionChange()
      }, 0)

      return
    }

    const currentSelection: Selection | null = window.getSelection()
    const hasActiveSelection: boolean = !!(
      currentSelection &&
      currentSelection.rangeCount > 0 &&
      !currentSelection.getRangeAt(0).collapsed &&
      currentSelection.toString().trim().length > 0
    )

    if (hasActiveSelection) {
      this.debugLog(' onEditorClick - preserving active selection, skipping cursor positioning')
      callbacks.onFormattingChange()
      callbacks.onSelectionChange()
      return
    }

    callbacks.onFormattingChange()

    setTimeout(() => {
      callbacks.onSelectionChange()
    }, 0)

    const selection: Selection | null = window.getSelection()
    if (!selection) {
      return
    }

    let range: Range | null = this.createRangeFromCaretPosition(event.clientX, event.clientY, editor)

    if (!range) {
      range = editorInteractionService.createRangeFromPoint(event.clientX, event.clientY, editor)
    }

    if (range) {
      requestAnimationFrame(() => {
        try {
          selection.removeAllRanges()
          selection.addRange(range!)

          const tagElement = (event.target as HTMLElement).closest(CSS_SELECTORS.TAG_ANCHOR) as HTMLElement
          if (tagElement) {
            tagElement.style.setProperty(
              TAG_EDITOR_STRINGS.CARET_COLOR_PROPERTY,
              TAG_EDITOR_COLORS.CARET_COLOR,
              TAG_EDITOR_STRINGS.CSS_IMPORTANT,
            )
          }

          this.debugLog('onEditorClick: Cursor positioned using Selection API')
        } catch (error) {
          this.debugLog('onEditorClick: Could not apply selection:', error)
        }
      })
    }
  }

  /**
   * Handles double-click events for inline tag editing
   */
  public handleDoubleClick(event: MouseEvent, context: EditorContext): void {
    event.stopPropagation()

    const { editorInteractionService, callbacks } = context
    const target = event.target as HTMLElement
    const tagElement = target.closest(CSS_SELECTORS.TAG_ANCHOR) as HTMLElement

    if (!tagElement) {
      return
    }

    event.preventDefault()

    let clickRange: Range | null = null

    if (typeof document.caretPositionFromPoint === 'function') {
      try {
        const caretPosition = document.caretPositionFromPoint(event.clientX, event.clientY)
        if (caretPosition && caretPosition.offsetNode && tagElement.contains(caretPosition.offsetNode)) {
          clickRange = document.createRange()
          clickRange.setStart(caretPosition.offsetNode, caretPosition.offset)
          clickRange.setEnd(caretPosition.offsetNode, caretPosition.offset)
        }
      } catch (error) {
        this.debugLog('onEditorDoubleClick: caretPositionFromPoint failed:', error)
      }
    }

    if (!clickRange) {
      const editor = context.editor
      clickRange = editorInteractionService.createRangeFromPoint(event.clientX, event.clientY, editor)
    }
    if (!clickRange) {
      clickRange = editorInteractionService.createRangeFromPoint(event.clientX, event.clientY, tagElement)
    }

    this.debugLog('onEditorDoubleClick: Entering edit mode', {
      tagId: tagElement.getAttribute(HTML_ATTRIBUTES.DATA_TAG_ID),
      hasRange: !!clickRange,
    })

    callbacks.onTagEdit(tagElement, clickRange || undefined)
  }

  /**
   * Handles keyboard events
   */
  public handleKeydown(event: KeyboardEvent, context: EditorContext): void {
    this.debugLog('Keydown event:', { key: event.key, code: event.code })

    const { stateService, editorInteractionService, tagManagerService, formattingService, pasteService, callbacks } =
      context
    const editor: HTMLElement | null = context.editor

    // Handle Escape during inline tag editing
    const editingTagEscape = stateService.getEditingTag()
    if (event.key === KEYBOARD_KEYS.ESCAPE && editingTagEscape) {
      event.preventDefault()
      if ('stopImmediatePropagation' in event && typeof event.stopImmediatePropagation === 'function') {
        event.stopImmediatePropagation()
      }
      event.stopPropagation()
      callbacks.onTagEditExit(true)
      return
    }

    // Handle backspace key
    if (event.key === KEYBOARD_KEYS.BACKSPACE) {
      const editingTag = stateService.getEditingTag()
      if (editingTag) {
        this.ngZone.runOutsideAngular(() => {
          const selection = window.getSelection()
          const savedRange = selection && selection.rangeCount > 0 ? selection.getRangeAt(0).cloneRange() : null

          const handled = editorInteractionService.handleBackspaceKey(
            event,
            editingTag,
            editor,
            (save: boolean) => callbacks.onTagEditExit(save),
            (tagElement: HTMLElement) => tagManagerService.moveCursorBeforeTag(tagElement),
          )
          if (handled) {
            return
          }

          if (savedRange) {
            requestAnimationFrame(() => {
              const newSelection = window.getSelection()
              if (newSelection) {
                try {
                  newSelection.removeAllRanges()
                  newSelection.addRange(savedRange)
                  if (editingTag.element) {
                    editingTag.element.style.setProperty(
                      TAG_EDITOR_STRINGS.CARET_COLOR_PROPERTY,
                      TAG_EDITOR_COLORS.CARET_COLOR,
                      TAG_EDITOR_STRINGS.CSS_IMPORTANT,
                    )
                  }
                } catch (error) {
                  this.debugLog(' Could not restore cursor after backspace:', error)
                }
              }
            })
          }
        })
      } else {
        const handled = editorInteractionService.handleBackspaceKey(
          event,
          null,
          editor,
          (save: boolean) => callbacks.onTagEditExit(save),
          (tagElement: HTMLElement) => tagManagerService.moveCursorBeforeTag(tagElement),
        )
        if (handled) {
          return
        }
      }

      const isEditing = stateService.getEditingTag()
      if (!isEditing) {
        requestAnimationFrame(() => {
          callbacks.onTagsParse()
        })
      }
    }

    // Handle Enter inside inline tag editing
    const editingTag = stateService.getEditingTag()
    if (event.key === KEYBOARD_KEYS.ENTER && editingTag) {
      event.preventDefault()
      if ('stopImmediatePropagation' in event && typeof event.stopImmediatePropagation === 'function') {
        event.stopImmediatePropagation()
      }
      event.stopPropagation()

      const inserted = formattingService.insertLineBreak()
      if (inserted) {
        const currentContent = callbacks.getEditorHTML()
        this.ngZone.runOutsideAngular(() => {
          callbacks.onContentUpdate(currentContent)
        })
      } else {
        this.debugLog('Failed to insert line break using Selection API')
      }
      return
    }

    // Handle delete key
    if (event.key === KEYBOARD_KEYS.DELETE) {
      const isEditing = stateService.getEditingTag()
      if (!isEditing) {
        setTimeout(() => {
          callbacks.onTagsParse()
        }, TAG_EDITOR_VALUES.CURSOR_EXIT_DELAY_MS)
      }
    }

    // Handle space key
    if (event.key === KEYBOARD_KEYS.SPACE) {
      if (stateService.getEditingTag()) {
        return
      }

      const selection = window.getSelection()
      if (!selection || selection.rangeCount === 0) {
        return
      }

      const range = selection.getRangeAt(0)
      const focusNode = range.endContainer
      const tagElement = editorInteractionService.findTagElement(focusNode, editor)

      if (
        !tagElement ||
        tagElement.getAttribute(HTML_ATTRIBUTES.CONTENTEDITABLE) === HTML_VALUES.CONTENTEDITABLE_TRUE
      ) {
        return
      }

      const handled = editorInteractionService.handleSpaceKey(event, editor, (tagElement: HTMLElement) =>
        tagManagerService.moveCursorOutsideTag(tagElement),
      )
      if (handled) {
        return
      }
    }

    // Handle arrow keys
    if (event.key === KEYBOARD_KEYS.ARROW_RIGHT) {
      const handled = editorInteractionService.handleArrowRightKey(event, editor, (tagElement: HTMLElement) =>
        tagManagerService.moveCursorOutsideTag(tagElement),
      )
      if (handled) {
        return
      }
    }

    if (event.key === KEYBOARD_KEYS.ARROW_LEFT) {
      const handled = editorInteractionService.handleArrowLeftKey(event, editor, (tagElement: HTMLElement) =>
        tagManagerService.moveCursorBeforeTag(tagElement),
      )
      if (handled) {
        return
      }
    }

    // Handle navigation keys
    if (NAVIGATION_KEYS.includes(event.key)) {
      return
    }

    // Handle Ctrl+X (Cut)
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === KEYBOARD_KEYS.CUT) {
      event.preventDefault()
      pasteService.handleCutOperation(
        () => callbacks.onTagsParse(),
        (content: string) => callbacks.debouncedEmitChange(content),
        () => callbacks.getEditorHTML(),
      )
      return
    }

    // Handle Ctrl+V (Paste) - handled by onEditorPaste method
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === KEYBOARD_KEYS.PASTE) {
      return
    }

    // Handle Ctrl+C (Copy)
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === KEYBOARD_KEYS.COPY) {
      return
    }
  }

  /**
   * Handles paste events
   */
  public handlePaste(event: ClipboardEvent, context: EditorContext): void {
    event.preventDefault()

    const { tagManagerService, formattingService, pasteService, callbacks } = context

    const clipboardData = event.clipboardData
    if (!clipboardData) {
      return
    }

    const html = clipboardData.getData('text/html')
    const text = clipboardData.getData('text/plain')

    if (html) {
      const htmlWithUniqueIds = tagManagerService.assignUniqueIdsToTagsInHTML(html, () =>
        tagManagerService.generateTagId(),
      )
      this.debugLog('HTML with unique IDs assigned to tags')

      const extractedTags = tagManagerService.extractTagDataFromHTML(htmlWithUniqueIds)
      this.debugLog('Extracted tags from HTML:', { count: extractedTags.length, tags: extractedTags })

      formattingService.insertHTML(htmlWithUniqueIds)

      setTimeout(() => {
        this.debugLog('Paste timeout triggered - parsing tags')
        callbacks.onTagsParse()
        callbacks.debouncedEmitChange(callbacks.getEditorHTML())
      }, TAG_EDITOR_VALUES.PASTE_RESTORE_DELAY_MS)
    } else if (text) {
      formattingService.insertText(text)

      setTimeout(() => {
        this.debugLog('Checking pasted plain text for tag matches:', {
          pastedText: text.substring(0, TAG_EDITOR_VALUES.CONTENT_PREVIEW_LENGTH),
        })

        if (context.editor) {
          pasteService.checkAndCreateTagFromPlainText(
            text,
            context.editor,
            callbacks.getTreeNodes(),
            () => tagManagerService.generateTagId(),
            () => callbacks.onTagsParse(),
          )
        }
      }, TAG_EDITOR_VALUES.PASTE_RESTORE_DELAY_MS)
    }
  }

  /**
   * Handles copy events
   */
  public handleCopy(event: ClipboardEvent, _context: EditorContext): void {
    const selection: Selection | null = window.getSelection()
    if (!selection || selection.rangeCount === 0) {
      return
    }

    const range: Range = selection.getRangeAt(0)
    if (range.collapsed) {
      return
    }

    const selectedContent: DocumentFragment = range.cloneContents()
    const tempDiv: HTMLDivElement = document.createElement('div')
    tempDiv.appendChild(selectedContent)
    const htmlContent: string = tempDiv.innerHTML

    this.debugLog(' Copying content:', htmlContent.substring(0, TAG_EDITOR_VALUES.CONTENT_PREVIEW_LENGTH))

    const clipboardData: DataTransfer | null = event.clipboardData
    if (clipboardData) {
      const plainText: string = tempDiv.textContent || tempDiv.innerText || TAG_EDITOR_STRINGS.EMPTY_STRING
      clipboardData.setData(TAG_EDITOR_STRINGS.TEXT_HTML_MIME_TYPE, htmlContent)
      clipboardData.setData(TAG_EDITOR_STRINGS.TEXT_PLAIN_MIME_TYPE, plainText)
    }
  }

  /**
   * Handles mouse down events
   */
  public handleMouseDown(event: MouseEvent, context: EditorContext): void {
    this.debugLog(' onEditorMouseDown:', {
      target: (event.target as HTMLElement)?.tagName,
      isSelectingText: context.isSelectingText.value,
    })
    context.isSelectingText.value = true
  }

  /**
   * Handles mouse move events
   */
  public handleMouseMove(event: MouseEvent, context: EditorContext): void {
    if (context.isSelectingText.value && event.buttons === 1) {
      const selection: Selection | null = window.getSelection()
      const hasRange: boolean = !!(selection && selection.rangeCount > 0)
      const isCollapsed: boolean = hasRange ? selection!.getRangeAt(0).collapsed : true
      const selectedText: string = selection?.toString() || ''
      const editorElement: HTMLElement | null = context.editor
      const selectionInEditor: boolean = !!(
        hasRange &&
        editorElement &&
        selection!.anchorNode &&
        editorElement.contains(selection!.anchorNode)
      )

      const hasSelection: boolean =
        selectionInEditor && (!!(selectedText && selectedText.trim().length > 0) || (hasRange && !isCollapsed))

      this.debugLog(' onEditorMouseMove (dragging):', {
        hasRange,
        isCollapsed,
        selectedTextLength: selectedText.length,
        hasSelection,
      })

      if (hasSelection) {
        context.callbacks.onSelectionChange()
      }
    }
  }

  /**
   * Handles mouse up events
   */
  public handleMouseUp(event: MouseEvent, context: EditorContext): void {
    this.debugLog(' onEditorMouseUp:', {
      target: (event.target as HTMLElement)?.tagName,
      isSelectingText: context.isSelectingText.value,
    })

    context.isSelectingText.value = false

    // Update selection state after mouse up (when selection is finalized)
    // Use timeout to ensure selection is finalized
    setTimeout(() => {
      context.callbacks.onSelectionChange()
      context.callbacks.onFormattingChange()
    }, 0)
  }
}
